import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Form,
  FormActions,
  FormSection,
  GridFrame,
  GridScroll,
  LoadingState,
  PageHeader,
  SelectField,
  StatusChip,
  Table,
  Td,
  TextField,
  Th,
  Tr,
  statusTone,
} from '@tessma/design-system';
import { Refusal, refusalText, formatDay, formatMoney, useAsync } from '../ui/async.js';
import type {
  Account,
  AccountingPeriod,
  FinanceGateway,
  FiscalYear,
  Journal,
  JournalLine,
  LedgerEntry,
  TrialBalance,
  UnreconciledBucket,
} from './gateway.js';

/**
 * THE LEDGER'S SCREENS: journals, the general ledger, the trial balance, the
 * chart of accounts and the accounting calendar.
 *
 * **NOTHING ON THESE SCREENS IS ADDED UP BY THE BROWSER.** The trial balance's
 * totals and the fact that it balances are the SERVER'S, computed in SQL over
 * exact integers inside the caller's own filtered relation. A browser that
 * summed the rows it happened to receive would produce a second answer — and a
 * trial balance whose whole value is that there is only one.
 *
 * **THERE IS NO "NEW JOURNAL" BUTTON**, because there is no endpoint behind
 * one. The ledger is written by the posting engine, reached through a
 * document: issue an invoice, post a bill, record a receipt.
 */

export function ChartOfAccountsScreen({
  gateway,
  legalEntityId,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
}) {
  const { state, reload } = useAsync<readonly Account[]>(
    () => gateway.accounts(legalEntityId),
    [gateway, legalEntityId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading the chart of accounts…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  // The shell's `<h1>` already reads "Chart of accounts"; see the note on the
  // sales invoice list for why this is no longer said a second time here.
  const header = <PageHeader description="The nominals this entity posts to." />;

  if (state.value.length === 0) {
    return (
      <>
        {header}
        <Card>
          <EmptyState icon="folder" title="No accounts" description="Within your reach." />
        </Card>
      </>
    );
  }

  return (
    <>
      {header}
      <Card padding="flush">
        <GridFrame>
          <GridScroll>
            <Table label="Chart of accounts">
              <thead>
                <Tr>
                  <Th>Code</Th>
                  <Th>Name</Th>
                  <Th>Type</Th>
                  <Th>Control</Th>
                  <Th>Active</Th>
                </Tr>
              </thead>
              <tbody>
                {state.value.map((account) => (
                  <Tr key={account.id}>
                    <Td>{account.code}</Td>
                    <Td>{account.name}</Td>
                    <Td variant="secondary">{account.accountType}</Td>
                    <Td variant="secondary">
                      {account.controlKind === null ? (
                        '—'
                      ) : (
                        <StatusChip tone="info">{account.controlKind}</StatusChip>
                      )}
                    </Td>
                    <Td variant="secondary">{account.isActive ? 'Yes' : 'No'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      </Card>
    </>
  );
}

export function FinancialCalendarScreen({
  gateway,
  legalEntityId,
  canManage,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId: string;
  readonly canManage: boolean;
}) {
  const { state, reload } = useAsync<{
    years: readonly FiscalYear[];
    periods: readonly AccountingPeriod[];
  }>(
    async () => ({
      years: await gateway.fiscalYears(legalEntityId),
      periods: await gateway.periods(legalEntityId),
    }),
    [gateway, legalEntityId],
  );
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [code, setCode] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [structure, setStructure] = useState<'monthly' | 'quarterly'>('monthly');
  const [opening, setOpening] = useState(false);
  const [opened, setOpened] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading the financial calendar…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  async function change(periodId: string, status: 'open' | 'closed') {
    setProblem(undefined);
    try {
      await gateway.setPeriodStatus(periodId, status);
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The server refused that.'));
    }
  }

  async function openYear() {
    setOpening(true);
    setProblem(undefined);
    setOpened(undefined);
    try {
      // NO DATES ARE COMPUTED HERE. The browser sends the start and the
      // structure; the server derives twelve months or four quarters and
      // refuses anything that does not tile the year exactly.
      const result = await gateway.openFiscalYear({
        legalEntityId,
        code,
        startsOn,
        structure,
      });
      setOpened(`${result.fiscalYear.code}: ${String(result.periods)} periods`);
      setCode('');
      setStartsOn('');
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The year could not be opened.'));
    } finally {
      setOpening(false);
    }
  }

  return (
    <div>
      <Card>
        {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
        <GridFrame>
          <GridScroll>
            <Table label="Financial years">
              <thead>
                <Tr>
                  <Th>Code</Th>
                  <Th>From</Th>
                  <Th>To</Th>
                  <Th>Status</Th>
                </Tr>
              </thead>
              <tbody>
                {state.value.years.map((year) => (
                  <Tr key={year.id}>
                    <Td>{year.code}</Td>
                    <Td variant="secondary">{formatDay(year.startsOn)}</Td>
                    <Td variant="secondary">{formatDay(year.endsOn)}</Td>
                    <Td>
                      <StatusChip tone={statusTone(year.status)}>{year.status}</StatusChip>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      </Card>

      {canManage ? (
        <Card>
          <CardHeader
            title="Open a financial year"
            subtitle="Until a year exists, every posting into it is refused for want of an open period — and opening one used to need database access."
          />
          {opened !== undefined ? <Alert tone="success" title={`Opened ${opened}`} /> : null}
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              void openYear();
            }}
          >
            <FormSection title="Year">
              <TextField
                label="Code"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                }}
                helper="The tenant’s own name for the year, unique within this entity."
                required
              />
              <TextField
                label="Starts on"
                type="date"
                value={startsOn}
                onChange={(event) => {
                  setStartsOn(event.target.value);
                }}
                helper="A year need not start in January: the periods are derived from this day."
                required
              />
              <SelectField
                label="Period structure"
                value={structure}
                onChange={(event) => {
                  setStructure(event.target.value as 'monthly' | 'quarterly');
                }}
                options={[
                  { value: 'monthly', label: 'Twelve calendar months' },
                  { value: 'quarterly', label: 'Four quarters' },
                ]}
              />
            </FormSection>
            <FormActions
              submitLabel="Open the year"
              submitting={opening}
              note="The server refuses a year that overlaps one this entity already has."
            />
          </Form>
        </Card>
      ) : null}

      <UnreconciledPanel gateway={gateway} legalEntityId={legalEntityId} />

      <Card padding="flush">
        <CardHeader
          title="Accounting periods"
          subtitle="Closing a period stops the next posting into it, not the next deployment."
        />
        <GridFrame>
          <GridScroll>
            <Table label="Accounting periods">
              <thead>
                <Tr>
                  <Th>#</Th>
                  <Th>From</Th>
                  <Th>To</Th>
                  <Th>Status</Th>
                  {canManage ? <Th>Action</Th> : null}
                </Tr>
              </thead>
              <tbody>
                {state.value.periods.map((period) => (
                  <Tr key={period.id}>
                    <Td variant="secondary">{period.ordinal}</Td>
                    <Td variant="secondary">{formatDay(period.startsOn)}</Td>
                    <Td variant="secondary">{formatDay(period.endsOn)}</Td>
                    <Td>
                      <StatusChip tone={statusTone(period.status)}>{period.status}</StatusChip>
                    </Td>
                    {canManage ? (
                      <Td>
                        <Button
                          variant="tertiary"
                          onClick={() => {
                            void change(period.id, period.status === 'open' ? 'closed' : 'open');
                          }}
                        >
                          {period.status === 'open' ? 'Close' : 'Reopen'}
                        </Button>
                      </Td>
                    ) : null}
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      </Card>
    </div>
  );
}

/**
 * WHAT A BLOCKED CLOSE IS COMPLAINING ABOUT (FR-REC-006).
 *
 * A period close consults the bank: an entity that has set a threshold will
 * not close over more unreconciled lines, or older ones, than it allows. The
 * refusal names the count and the age — and this panel is where somebody goes
 * to see WHICH lines, which is the difference between a gate people clear and
 * a gate people learn to override.
 *
 * IT IS NOT THE CONTROL. The threshold is checked in the domain, inside the
 * transaction, so the same refusal meets a caller arriving by any route.
 */
function UnreconciledPanel({
  gateway,
  legalEntityId,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId: string;
}) {
  const { state, reload } = useAsync<readonly UnreconciledBucket[]>(
    () => gateway.unreconciledAgeing(legalEntityId),
    [gateway, legalEntityId],
  );

  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  if (state.value.length === 0) {
    return (
      <Card>
        <CardHeader title="Unreconciled at the bank" />
        <Alert tone="success" title="Nothing outstanding">
          Every bank line within your reach has been reconciled, so no close will be blocked on this
          entity&rsquo;s account of it.
        </Alert>
      </Card>
    );
  }

  return (
    <Card padding="flush">
      <CardHeader
        title="Unreconciled at the bank"
        subtitle="A close is refused where these exceed what this entity allows. Reconciling them is how the gate is cleared."
      />
      <GridFrame>
        <GridScroll>
          <Table label="Unreconciled bank lines">
            <thead>
              <Tr>
                <Th>Account</Th>
                <Th>Age</Th>
                <Th align="right">Lines</Th>
                <Th align="right">Amount</Th>
                <Th align="right">Oldest (days)</Th>
              </Tr>
            </thead>
            <tbody>
              {state.value.map((bucket) => (
                <Tr key={`${bucket.bankAccountId}:${bucket.bucket}`}>
                  <Td>{bucket.bankAccountName}</Td>
                  <Td>{UNRECONCILED_BANDS[bucket.bucket] ?? bucket.bucket}</Td>
                  <Td variant="money">{bucket.lines}</Td>
                  <Td variant="money">{formatMoney(bucket.amount, 'GBP')}</Td>
                  <Td variant="money">{bucket.oldestDays}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </GridScroll>
      </GridFrame>
    </Card>
  );
}

const UNRECONCILED_BANDS: Readonly<Record<string, string>> = {
  days_0_7: 'This week',
  days_8_30: '8–30 days',
  days_31_90: '31–90 days',
  days_90_plus: 'Over 90 days',
};

export function JournalsScreen({
  gateway,
  legalEntityId,
  onOpen,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
  readonly onOpen: (journalId: string) => void;
}) {
  const { state, reload } = useAsync<readonly Journal[]>(
    () => gateway.journals(legalEntityId),
    [gateway, legalEntityId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading journals…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  if (state.value.length === 0) {
    return (
      <Card>
        <CardHeader title="Journals" />
        <EmptyState
          icon="folder"
          title="No journals"
          description="A journal is written by the posting engine when a document is posted."
        />
      </Card>
    );
  }

  return (
    <Card padding="flush">
      <CardHeader
        title="Journals"
        subtitle="Every ledger movement, and the document that caused it."
      />
      <GridFrame>
        <GridScroll>
          <Table label="Journals">
            <thead>
              <Tr>
                <Th>Number</Th>
                <Th>Posted</Th>
                <Th>Description</Th>
                <Th>Source</Th>
                <Th>Status</Th>
              </Tr>
            </thead>
            <tbody>
              {state.value.map((journal) => (
                <Tr key={journal.id}>
                  <Td>
                    <Button
                      variant="tertiary"
                      onClick={() => {
                        onOpen(journal.id);
                      }}
                    >
                      {/*
                        A JOURNAL WITH NO NUMBER IS A DRAFT, and saying so is
                        the truth. Eight characters of a uuid look like an
                        identifier somebody could quote, and nobody can.
                      */}
                      {journal.journalNumber ?? 'Draft'}
                    </Button>
                  </Td>
                  <Td variant="secondary">{formatDay(journal.postingDate)}</Td>
                  <Td>{journal.description ?? '—'}</Td>
                  <Td variant="secondary">{journal.sourceType ?? 'manual'}</Td>
                  <Td>
                    <StatusChip tone={statusTone(journal.status)}>{journal.status}</StatusChip>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </GridScroll>
      </GridFrame>
    </Card>
  );
}

export function JournalDetailScreen({
  gateway,
  journalId,
}: {
  readonly gateway: FinanceGateway;
  readonly journalId: string;
}) {
  /**
   * A JOURNAL LINE NAMED ITS ACCOUNT BY EIGHT CHARACTERS OF A UUID.
   *
   * A journal is the one place in the product where a person checks what was
   * posted WHERE, and the account column was the part they could not read. The
   * codes and names come from the chart of accounts — the server's own list,
   * already through the same predicate — so this is a lookup between two of
   * the server's answers rather than the browser naming anything.
   *
   * An account the list does not cover keeps its id: a line was posted to it,
   * and showing a blank would suggest it was posted nowhere.
   */
  const { state, reload } = useAsync<{
    journal: Journal;
    lines: readonly JournalLine[];
    accounts: ReadonlyMap<string, string>;
  }>(async () => {
    const { journal, lines } = await gateway.journal(journalId);
    // Every account within reach — the journal itself names no entity, and
    // the predicate scopes the chart anyway. A failed chart read is not a
    // failed journal: the lines are still true without the names.
    const chart = await gateway.accounts().catch(() => []);
    return {
      journal,
      lines,
      accounts: new Map(chart.map((account) => [account.id, `${account.code} ${account.name}`])),
    };
  }, [gateway, journalId]);

  if (state.status === 'loading') return <LoadingState title="Loading journal…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { journal, lines, accounts } = state.value;

  return (
    <Card padding="flush">
      <CardHeader
        title={journal.journalNumber ?? 'Journal'}
        subtitle={`${formatDay(journal.postingDate)} · ${journal.description ?? ''}`}
      />
      <GridFrame>
        <GridScroll>
          <Table label="Journal lines">
            <thead>
              <Tr>
                <Th>#</Th>
                <Th>Account</Th>
                <Th>Description</Th>
                <Th align="right">Debit</Th>
                <Th align="right">Credit</Th>
              </Tr>
            </thead>
            <tbody>
              {/*
                THE ROW NUMBER IS THE POSITION IN THE ANSWER, because the server
                does not send a line number and never did. The lines arrive
                ordered by `line_no` in SQL, so the position IS the order the
                journal was written in — what it must not do is claim to be a
                stored identifier that does not exist.
              */}
              {lines.map((line, index) => (
                <Tr key={`${journal.id}:${String(index)}`}>
                  <Td variant="secondary">{index + 1}</Td>
                  <Td>{accounts.get(line.accountId) ?? line.accountId}</Td>
                  <Td>{line.description ?? '—'}</Td>
                  <Td variant="money">{formatMoney(line.debitTxn, journal.currency)}</Td>
                  <Td variant="money">{formatMoney(line.creditTxn, journal.currency)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </GridScroll>
      </GridFrame>
    </Card>
  );
}

export function GeneralLedgerScreen({
  gateway,
  legalEntityId,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
}) {
  const { state, reload } = useAsync<readonly LedgerEntry[]>(
    () => gateway.ledgerEntries(legalEntityId === undefined ? {} : { legalEntityId }),
    [gateway, legalEntityId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading the general ledger…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const header = <PageHeader description="Every entry you are permitted to see." />;

  if (state.value.length === 0) {
    return (
      <>
        {header}
        <Card>
          <EmptyState icon="folder" title="No entries" description="Within your reach." />
        </Card>
      </>
    );
  }

  return (
    <>
      {header}
      <Card padding="flush">
        <GridFrame>
          <GridScroll>
            <Table label="General ledger">
              <thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Account</Th>
                  <Th>Description</Th>
                  <Th>Journal</Th>
                  <Th align="right">Debit</Th>
                  <Th align="right">Credit</Th>
                </Tr>
              </thead>
              <tbody>
                {state.value.map((entry) => (
                  <Tr key={entry.id}>
                    <Td variant="secondary">{formatDay(entry.postingDate)}</Td>
                    <Td>
                      {entry.accountCode} {entry.accountName}
                    </Td>
                    <Td>{entry.description ?? '—'}</Td>
                    <Td variant="secondary">{entry.journalNumber ?? '—'}</Td>
                    <Td variant="money">{formatMoney(entry.debit, 'GBP')}</Td>
                    <Td variant="money">{formatMoney(entry.credit, 'GBP')}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      </Card>
    </>
  );
}

export function TrialBalanceScreen({
  gateway,
  legalEntityId,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId: string;
}) {
  const { state, reload } = useAsync<TrialBalance>(
    () => gateway.trialBalance(legalEntityId),
    [gateway, legalEntityId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading the trial balance…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { rows, totals } = state.value;

  return (
    <Card padding="flush">
      <CardHeader
        title="Trial balance"
        subtitle="Computed by the server over your own authorised entries."
      />
      {/*
        THE SERVER SAID WHETHER IT BALANCES. This screen renders that answer; it
        does not add the rows up to check, because a second arithmetic is a
        second answer and a trial balance exists to have one.
      */}
      <Alert
        tone={totals.balanced ? 'success' : 'danger'}
        title={
          totals.balanced ? 'The trial balance balances' : 'The trial balance does not balance'
        }
      >
        Debits {formatMoney(totals.debit, 'GBP')} · Credits {formatMoney(totals.credit, 'GBP')}
      </Alert>
      {rows.length === 0 ? (
        <EmptyState icon="chart-bar" title="No entries" description="Within your reach." />
      ) : (
        <GridFrame>
          <GridScroll>
            <Table label="Trial balance">
              <thead>
                <Tr>
                  <Th>Code</Th>
                  <Th>Account</Th>
                  <Th>Type</Th>
                  <Th align="right">Debit</Th>
                  <Th align="right">Credit</Th>
                </Tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Tr key={row.accountId}>
                    <Td>{row.accountCode}</Td>
                    <Td>{row.accountName}</Td>
                    <Td variant="secondary">{row.accountType}</Td>
                    <Td variant="money">{formatMoney(row.debit, 'GBP')}</Td>
                    <Td variant="money">{formatMoney(row.credit, 'GBP')}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      )}
    </Card>
  );
}
